<template>
    <div>
        <!-- MAIN MODAL - SHOW TOPIC WITH ANSWERS -->
        <q-modal ref="modalbody" v-model="show_modal" :content-css="{minWidth: getMinWidth, maxWidth: getMaxWidth, minHeight: '90vh'}" @hide="hideModal()">
            <q-modal-layout>
                <q-toolbar slot="header">
                    <q-btn flat round dense v-close-overlay icon="close" />
                    <q-toolbar-title>{{ $t('topic') + ': ' + topic.title }}</q-toolbar-title>
                </q-toolbar>

                <div class="row">
                    <div class="col-12">
                        <q-list>
                            <q-item dense multiline>
                            <q-item-side class="q-pa-xs" v-if="topic.avatar" :avatar="topic.avatar" />
                            <q-item-side class="q-pa-xs" v-else :avatar="getPlaceholderAvatar" />
                            <q-item-main>
                                <q-item-tile sublabel lines="2">
                                    <span class="text-light" v-if="$q.platform.is.desktop">{{ $t('added_by') + ' ' }}</span>
                                    <span class="text-italic text-bold">{{ topic.author }}</span>
                                </q-item-tile>
                            </q-item-main>
                            <q-item-side right>
                                <q-item-tile stamp>
                                    <span v-if="$q.platform.is.desktop">{{ $t('created_in') + ': '}}</span>
                                    <span>{{ topic.updated_date }}</span>
                                </q-item-tile>
                                <q-item-tile>
                                    <q-btn class="q-pa-sm" v-if="topic.report" flat rounded color="negative" icon="fas fa-flag alt" @click="cancel_report_topic = true">
                                        <q-tooltip>{{ this.$t('topic_reported') }}</q-tooltip>
                                    </q-btn>
                                </q-item-tile>
                            </q-item-side>
                            </q-item>
                            <q-item dense multiline>
                                <q-item-main>
                                    <q-item-tile class="text-center text-bold" label lines="1">{{ topic.title }}</q-item-tile>
                                    <viewer class="reply q-pt-xs" v-if="markdown_enabled" :text="renderQuestion" />
                                    <p v-if="!markdown_enabled" class="reply q-pt-xs">{{ renderQuestion }}</p>
                                </q-item-main>
                            </q-item>
                            <q-item sparse>
                                <q-item-main class="q-mt-md">
                                    <p class="caption"><strong>{{ $t('topic_status') }}</strong></p>
                                    <q-btn-toggle v-model="topic.status" :toggle-color="getColorStatus(topic.status)" :options="topic_statuses" @input="setTopicStatus(topic.status)" />
                                </q-item-main>
                            </q-item>
                        </q-list>
                    </div>
                </div>

                <div class="row">
                    <div v-if="answers && answers.length > 0" class="col-xs-12 col-sm-12 q-my-md text-center">
                        <p>{{ $t('answers').toUpperCase() }}</p>
                    </div>
                    <div class="col-12" ref="answersBody" v-if="answers && answers.length > 0">
                        <q-list v-for="(answer, index) in answers" :key="index" class="text-align-center">
                            <q-item dense multiline>
                            <q-item-side class="q-pa-xs" v-if="answer.avatar" :avatar="answer.avatar" />
                            <q-item-side class="q-pa-xs" v-else :avatar="getPlaceholderAvatar" />
                            <q-item-main>
                                <q-item-tile sublabel lines="2">
                                    <span class="text-light" v-if="$q.platform.is.desktop">{{ $t('replied_by') + ' ' }}</span>
                                    <span class="text-italic text-bold">{{ answer.author }}</span>
                                </q-item-tile>
                            </q-item-main>
                            <q-item-side right>
                                <q-item-tile stamp>
                                    <span v-if="$q.platform.is.desktop">{{ $t('replied_in') + ': '}}</span>
                                    {{ answer.updated_date }}
                                </q-item-tile>
                                <q-item-tile>
                                    <q-btn class="q-pa-sm" v-if="answer.report" flat rounded color="negative" icon="fas fa-flag alt" @click="setAnswerToCancelReport(answer)">
                                        <q-tooltip>{{ $t('answer_reported') }}</q-tooltip>
                                    </q-btn>
                                </q-item-tile>
                            </q-item-side>
                            </q-item>
                            <q-item dense>
                                <q-item-main>
                                    <viewer class="reply q-pt-xs" :text="answer.answer" />
                                </q-item-main>
                            </q-item>
                            <q-item sparse>
                                <q-item-main class="q-mt-md">
                                    <p class="caption"><strong>{{ $t('answer_status') }}</strong></p>
                                    <q-btn-toggle v-model="answer.status" :toggle-color="getColorStatus(answer.status)" :options="answer_statuses" @input="setAnswerStatus(answer)" />
                                </q-item-main>
                            </q-item>
                        </q-list>
                    </div>
                </div>

                <q-toolbar slot="footer">
                    <q-btn class="q-mx-sm" color="negative" :label="$t('close')" @click="hideModal()" />
                </q-toolbar>

            </q-modal-layout>
        </q-modal>

        <q-dialog v-model="cancel_report_topic">
            <span slot="title">{{ $t('uncheck_reported_topic') }}</span>

            <span slot="message">{{ $t('confirm_uncheck_reported_topic') }}</span>

            <template slot="buttons" slot-scope="props">
                <q-btn color="negative" :label="$t('cancel')" @click="cancel_report_topic = false" />
                <q-btn color="positive" :label="$t('confirm')" @click="cancelTopicReport" />
            </template>
        </q-dialog>

        <q-dialog v-model="cancel_report_answer">
            <span slot="title">{{ $t('uncheck_reported_answer') }}</span>

            <span slot="message">{{ $t('confirm_uncheck_reported_answer') }}</span>

            <template slot="buttons" slot-scope="props">
                <q-btn color="negative" :label="$t('cancel')" @click="cancel_report_answer = false" />
                <q-btn color="positive" :label="$t('confirm')" @click="cancelAnswerReport(answer_report_id)" />
            </template>
        </q-dialog>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { date } from 'quasar'

export default {
    props: ['topic', 'show'],

    data() {
        return {
            loading: false,
            cancel_report_topic: false,
            cancel_report_answer: false,
            answer_report_id: null,
            answers: [],
            question: '',
            new_uid: 1,
            markdown_enabled: false,
            topic_statuses: [
                { label: this.$t('published'), value: 1 },
                { label: this.$t('unpublished'), value: 2 },
                { label: this.$t('deleted'), value: 3 },
                { label: this.$t('closed'), value: 4 }
            ],
            answer_statuses: [
                { label: this.$t('published_f'), value: 1 },
                { label: this.$t('unpublished_f'), value: 2 },
                { label: this.$t('deleted_f'), value: 3 }
            ]
        }
    },

    components: {
        'viewer': () => import('components/editor/Viewer')
    },

    computed: {
        ...mapGetters({
            access_token: 'common/access_token',
            clients: 'params/clients',
            id_user_client_selected: 'common/id_user_client_selected',
            id: 'common/id',
        }),

        renderQuestion() {
            return this.question
        },

        getHeader() {
            let headers = {}
            return headers = {
                headers: {
                    'content-type': 'application/json',
                    'authorization': 'Basic ' + btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
                    'authorization': 'Bearer ' + this.access_token
                }
            }
        },

        show_modal: {
            get() {
                return this.show
            },
            set(val) {
                this.$emit('hideModal', '')
            }
        },

        getMaxWidth() {
            if (this.$q.platform.is.mobile) return '100vw'
            else return '84vw'
        },

        getMinWidth() {
            if (!this.$q.platform.is.mobile) return '70vw'
        },

        getPlaceholderAvatar() {
            let client = this.clients.find((obj) => { return obj.id === this.id_user_client_selected })

			if (client) {
                return client.user_avatar_placeholder
            }
			else return ''
        },

        // editableAnswer() {
        //     return this.answers.filter(ans => this.id === ans.author_id)
        // }
    },

    methods: {
        setMessage(val) {
            this.message = val
        },

        hideModal() {
            this.answers = []
            this.$emit('hideModal', '')
        },

        getColorStatus(val) {
            if (val == 1) return 'positive'
            else if (val == 2) return 'warning'
            else return 'negative'
        },

        setTopicStatus(status) {
            this.$q.loading.show()
            this.loading = true

            let newStatus = { status: status}

            this.$axios.post(`/admin_group_trainings/group_trainings/${this.$route.params.id}/group_training_forum/${this.topic.id}/set_status_topic/`, newStatus, this.getHeader)
            .then(response => {
                this.$q.notify({
                    message: this.$t('topic_status_changed'),
                    type: 'positive',
                    icon: 'fas fa-check'
                })
            })
            .catch(e => {
                this.$q.notify({
                    message: this.$t('error_changing_topic_status'),
                    type: 'negative',
                    icon: 'fal fa-exclamation'
                })
                console.log(e)
            })
            .finally(() => {
                this.$q.loading.hide()
                this.loading = false
                this.hideModal()
            })
        },

        setAnswerStatus(answer) {
            this.$q.loading.show()
            this.loading = false

            let newStatus = { status: answer.status}

            this.$axios.post(`/admin_group_trainings/group_trainings/${this.$route.params.id}/group_training_forum/${this.topic.id}/answer/${answer.answer_id}/set_status_answer/`, newStatus, this.getHeader)
            .then(response => {
                this.$q.notify({
                    message: this.$t('answer_status_changed'),
                    type: 'positive',
                    icon: 'fas fa-check'
                })
            })
            .catch(e => {
                this.$q.notify({
                    message: this.$t('error_changing_answer_status'),
                    type: 'negative',
                    icon: 'fal fa-exclamation'
                })
                console.log(e)
            })
            .finally(() => {
                this.$q.loading.hide()
                this.loading = false
                this.fetchTopic()
            })
        },

        cancelTopicReport() {
            this.cancel_report_topic = false
            this.$q.loading.show()
            this.loading = true

            let empty = { }

            this.$axios.post(`/admin_group_trainings/group_trainings/${this.$route.params.id}/group_training_forum/${this.topic.id}/unset_topic_report/`, empty, this.getHeader)
            .then(response => {
                this.$q.notify({
                    message: this.$t('topic_report_removed'),
                    type: 'positive',
                    icon: 'fas fa-check'
                })
            })
            .catch(e => {
                this.$q.notify({
                    message: this.$t('error_removing_topic_report'),
                    type: 'negative',
                    icon: 'fal fa-exclamation'
                })
                console.log(e)
            })
            .finally(() => {
                this.$q.loading.hide()
                this.loading = false
                this.hideModal()
                this.fetchTopic()
            })

        },

        setAnswerToCancelReport(answer) {
            this.answer_report_id = answer.answer_id
            this.cancel_report_answer = true
        },

        cancelAnswerReport() {
            this.cancel_report_answer = false
            this.$q.loading.show()
            this.loading = true

            let empty = { }

            this.$axios.post(`/admin_group_trainings/group_trainings/${this.$route.params.id}/group_training_forum/${this.topic.id}/answer/${this.answer_report_id}/unset_topic_answer_report/`, empty, this.getHeader)
            .then(response => {
                this.$q.notify({
                    message: this.$t('answer_report_removed'),
                    type: 'positive',
                    icon: 'fas fa-check'
                })
            })
            .catch(e => {
                this.$q.notify({
                    message: this.$t('error_removing_answer_report'),
                    type: 'negative',
                    icon: 'fal fa-exclamation'
                })
                console.log(e)
            })
            .finally(() => {
                this.$q.loading.hide()
                this.loading = false
                this.fetchTopic()
            })
        },

        fetchTopic() {
            this.loading = true
            this.$q.loading.show()

            this.new_title = this.topic.title

            this.$axios.get(`/admin_group_trainings/group_trainings/${this.$route.params.id}/group_training_forum/${this.topic.id}/list_topic_and_answers/`, this.getHeader)
            .then(response => {
                this.question = response.data.question
                this.answers = response.data.answers

                if (this.answers.length) {
                    this.answers = this.answers.map(ans => {
                        return {
                            status: ans.status,
                            author_id: ans.author.id,
                            answer: ans.answer,
                            answer_id: ans.id,
                            liked: ans.liked,
                            likes: ans.likes,
                            report: ans.report,
                            author: ans.author.name,
                            avatar: ans.author.author_image,
                            updated_date: date.formatDate(ans.updated_date, 'DD/MM/YYYY HH:mm'),
                        }
                    })
                }
            })
            .catch(e => {
                console.log(e)
            })
            .finally(() => {
                this.markdown_enabled = true
                this.loading = false
                this.$q.loading.hide()
            })
        },
    },

    beforeDestroy() {
        if (this.answers.length) {
            this.answers = []
        }
        this.message = ''
        this.markdown_enabled = false
    },

    mounted () {
        if (this.topic) this.fetchTopic()
    },

}
</script>

<style lang="stylus" scoped>

</style>
